import { isReadOnlyColumnType } from './constants';

/**
 * Shared vocabulary for PDF template overlays.
 *
 * One place for the source model, the styling that encodes it, and the geometry
 * helpers — so `PdfFieldsPalette`, `PdfCanvas` and `PdfOverlaySettingsPanel`
 * cannot drift on what a "form-sourced" box looks like or where it sits.
 */

/**
 * Where an overlay's value comes from.
 *
 * Fixed at drop time by which palette section the chip was dragged from, and
 * never editable afterwards. That is what makes the illegal state — a
 * submission-sourced value for a column that is not on the form —
 * unrepresentable. Changing source means delete and re-drag.
 */
export const SOURCE = {
  FORM: 'form',
  ITEM: 'item',
};

/**
 * Visual language per source. Three redundant cues — colour, border style and
 * icon — so the distinction survives greyscale printing and colour blindness.
 *
 * Dashed reads as "a value that arrives later from a person"; solid as "board
 * data that is already known".
 */
export const SOURCE_STYLE = {
  [SOURCE.FORM]: {
    label: 'Answer from form submission',
    short: 'Form submission',
    border: '1px dashed #0073ea',
    background: 'rgba(0, 115, 234, 0.08)',
    color: '#0073ea',
    chipBackground: 'rgba(0, 115, 234, 0.08)',
    chipBorder: '1px dashed rgba(0, 115, 234, 0.45)',
  },
  [SOURCE.ITEM]: {
    label: 'Value from item column',
    short: 'Item column',
    border: '1px solid #676879',
    background: 'rgba(103, 104, 121, 0.08)',
    color: '#676879',
    chipBackground: 'rgba(103, 104, 121, 0.06)',
    chipBorder: '1px solid rgba(103, 104, 121, 0.35)',
  },
};

/** Default box size for a newly placed overlay, as a fraction of the page. */
export const DEFAULT_OVERLAY_SIZE = { w: 0.32, h: 0.022 };

export const DEFAULT_FONT_SIZE = 10;

/**
 * What happens when a value does not fit its box AFTER wrapping.
 *
 * Wrapping itself is not a choice — text always wraps, and the box's HEIGHT
 * decides how many lines it may use (see `maxLinesFor`). These two only decide
 * the fallback once those lines are full:
 *   shrink   — reduce the font until it fits; keeps the whole answer
 *   truncate — keep the size and cut, marking the cut with an ellipsis
 *
 * There used to be a third, `Wrap`, from when wrapping was opt-in. Stored rows
 * may still carry it; everything treats it as `shrink`.
 */
export const OVERFLOW_OPTIONS = [
  { value: 'shrink', label: 'Shrink to fit' },
  { value: 'truncate', label: 'Truncate' },
];

/** Ratio between font size and line spacing. Mirrors pdf-stamp.service.ts. */
export const LINE_HEIGHT_RATIO = 1.15;

/**
 * How many lines of `fontSizePt` text a box `boxHeightPt` tall can hold.
 *
 * Both units are PDF POINTS, not CSS pixels — the same numbers the stamper
 * works in — so the respondent view and the generated document agree on whether
 * a box is single- or multi-line. Always at least 1: a box too short for even
 * one line still gets one rather than rendering nothing.
 */
export function maxLinesFor(boxHeightPt, fontSizePt = DEFAULT_FONT_SIZE) {
  const lineHeight = (fontSizePt || DEFAULT_FONT_SIZE) * LINE_HEIGHT_RATIO;
  return Math.max(1, Math.floor(boxHeightPt / lineHeight));
}

export const ALIGN_OPTIONS = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

/**
 * Smallest allowed box, as a fraction of the page.
 *
 * Mirrors `sanitiseOverlay` on the server: below this a box is invisible in the
 * editor and stamps nothing, and the server rejects the row outright. Clamping
 * to the same floor here is what stops the panel from displaying a size that a
 * save would silently change.
 */
export const MIN_OVERLAY_W = 0.01;
export const MIN_OVERLAY_H = 0.005;

/**
 * A placement's size in PDF POINTS, given its page's own size in points.
 *
 * Points, not pixels or percentages, because that is the unit the owner already
 * reasons in: `fontSize` is points, and `maxLinesFor` divides box height by font
 * size. A 40pt box holding 10pt text is legibly three lines; "0.048 of the page"
 * is not a thing anyone can size a signature line with.
 */
export function overlaySizePt(overlay, pagePt) {
  return {
    w: (overlay.w || 0) * pagePt.width,
    h: (overlay.h || 0) * pagePt.height,
  };
}

/**
 * Patch resizing a placement to the given PDF-point size, top-left corner fixed.
 *
 * Growth is capped at the page edge rather than allowed to push the box back
 * onto the page. `toNormalised` does shift x — correct for a drag, where the
 * whole box is in the owner's hand — but wrong for typing a width: nudging one
 * number should never relocate a box that was deliberately positioned. So width
 * stops at the margin and the box stays put.
 *
 * The `1 - x` ceiling can never fall below the floor: the server stores
 * `x = min(x, 1 - w)`, so `1 - x >= w >= MIN_OVERLAY_W` always holds.
 */
export function resizeOverlayPt(overlay, pagePt, next) {
  const patch = {};
  if (Number.isFinite(next.w)) {
    patch.w = Math.min(Math.max(next.w / pagePt.width, MIN_OVERLAY_W), 1 - overlay.x);
  }
  if (Number.isFinite(next.h)) {
    patch.h = Math.min(Math.max(next.h / pagePt.height, MIN_OVERLAY_H), 1 - overlay.y);
  }
  return patch;
}

/**
 * Split the form's columns into the palette's two sections.
 *
 * Both come from `form.fields`, which already holds every board column the form
 * knows about — so no extra request is needed.
 *
 * The split is "can a respondent answer this?", not merely "is it on the form?".
 * On the form and writable → the answer comes from the submission. Otherwise —
 * not on the form, or computed by monday — the value comes from the item.
 *
 * This derivation is what guarantees the two sections never overlap, and is why
 * source can be fixed by section rather than chosen per field.
 */
export function splitPaletteFields(fields) {
  const columns = (fields || []).filter((f) => !f.isSection && f.id);
  return {
    /*
     * On the form AND actually answerable by a respondent.
     *
     * The read-only exclusion matters: monday computes mirror / formula /
     * board_relation and never accepts a write, so even when such a column sits
     * on the form it is showing the ITEM's value, not collecting one. Listing it
     * as a form field invited a placement that could only ever stamp blank.
     * Routed below instead, where it works.
     */
    formFields: columns.filter((f) => f.visible === true && !isReadOnlyColumnType(f.type)),
    /*
     * Everything whose value comes from the item: columns not on the form, plus
     * the computed ones above regardless of whether they are on it.
     */
    itemColumns: columns.filter((f) => f.visible !== true || isReadOnlyColumnType(f.type)),
  };
}

/** Unique key for a placed field+source pair — a field may sit on several pages. */
export function placementKey(source, fieldId) {
  return `${source}:${fieldId}`;
}

/** The set of field+source pairs that already have at least one overlay. */
export function placedKeys(overlays) {
  return new Set((overlays || []).map((o) => placementKey(o.source, o.fieldId)));
}

/**
 * A form-sourced overlay whose field is no longer on the form.
 *
 * Happens when a field is removed on the Edit tab after being mapped. The
 * overlay can never resolve a value, so it is flagged in the editor and skipped
 * at generation rather than stamped blank.
 */
export function isOrphanOverlay(overlay, formFieldIds) {
  return overlay.source === SOURCE.FORM && !formFieldIds.has(overlay.fieldId);
}

/**
 * Convert normalised (0..1) overlay coordinates to pixels for a rendered page.
 *
 * Storage is normalised because the editor renders at whatever width fits the
 * canvas while the backend draws in PDF points; keeping fractions is what makes
 * the two agree at any zoom or DPI.
 */
export function toPixels(overlay, pageWidth, pageHeight) {
  return {
    left: overlay.x * pageWidth,
    top: overlay.y * pageHeight,
    width: overlay.w * pageWidth,
    height: overlay.h * pageHeight,
  };
}

/** Inverse of `toPixels`, clamped so a box cannot be dragged off the page. */
export function toNormalised(px, pageWidth, pageHeight) {
  const w = Math.min(Math.max(px.width / pageWidth, 0.01), 1);
  const h = Math.min(Math.max(px.height / pageHeight, 0.005), 1);
  return {
    x: Math.min(Math.max(px.left / pageWidth, 0), 1 - w),
    y: Math.min(Math.max(px.top / pageHeight, 0), 1 - h),
    w,
    h,
  };
}

/** Stable-enough id for a new overlay; the server re-checks and may replace it. */
export function newOverlayId(source, fieldId) {
  return `${source}-${fieldId}-${Math.random().toString(36).slice(2, 9)}`;
}

// ── Stamped-text parity ──────────────────────────────────────────────────────
// The Fill-on-PDF view only admits a field type if the control can display
// exactly the string the server will stamp. These helpers are that string.
//
// They MIRROR apr-backend/src/utils/pdf/column-display.ts. Any change to how a
// type is stamped must change both, or the respondent signs one thing and the
// document records another. The backend stays the authority; this exists only
// because the browser has to render the same value before submission.

/** Rating columns carry their maximum under one of two keys; Monday's default is 5. */
export function ratingMax(field) {
  return Number(field?.settings?.rating_count ?? field?.ratingCount ?? 5) || 5;
}

/** The exact text the stamper draws for a rating, e.g. "4 / 5". */
export function ratingText(field, value) {
  const n = Number(value || 0);
  return n > 0 ? `${n} / ${ratingMax(field)}` : '';
}

/** The exact text the stamper draws for a checkbox. */
export function booleanText(checked) {
  return checked ? 'Yes' : 'No';
}

/** The exact text the stamper draws for a dropdown: selected labels, comma-joined. */
export function dropdownText(labels) {
  return (labels || []).filter(Boolean).join(', ');
}
